import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import frIcon from "@/assets/fr-icon.svg";
import enIcon from "@/assets/en-icon.svg";
import { Input } from "@/components/ui/input";
import { catchError } from "@/lib/errors";
import { queryClient } from "@/lib/query-client";
import { dilemmasRepo } from "@/lib/repositories";
import { handleApiError } from "@/utils/errors";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as z from "zod";
import { cn } from "@/lib/utils";
import { useThemesList } from "@/features/themes/hooks/useThemesList";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ROUTES } from "@/constants/routes";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Loader, Undo } from "lucide-react";
import { useDilemmasFormStore } from "../../stores/useDilemmaFormStore";
import { DILEMMAS_STATUS } from "../../constants";
import { hasStep1Changes } from "../../utils";
import BottomFixed from "@/components/bottom-fixed";
import { StoreKeys } from "@/lib/local-store";

const DilemmaFormStepOne = () => {
  const { t } = useTranslation();
  const currentStep = useDilemmasFormStore((s) => s.currentStep);
  const setCurrentStep = useDilemmasFormStore((s) => s.setCurrentStep);
  const step1Data = useDilemmasFormStore((s) => s.step1Data);
  const setStep1Data = useDilemmasFormStore((s) => s.setStep1Data);
  const createdDilemma = useDilemmasFormStore((s) => s.createdDilemma);
  const initialDilemma = useDilemmasFormStore((s) => s.initialDilemma);
  const setCreatedDilemma = useDilemmasFormStore((s) => s.setCreatedDilemma);
  const isEditMode = useDilemmasFormStore((s) => s.isEditMode);
  const reviewComment = useDilemmasFormStore((s) => s.reviewComment);
  const setIsStep1Dirty = useDilemmasFormStore((s) => s.setIsStep1Dirty);
  const reset = useDilemmasFormStore((s) => s.reset);
  const navigate = useNavigate();
  const { themes: fetchedThemes, isLoading: isLoadingThemes } = useThemesList();
  const [isLoading, setIsLoading] = useState(false);
  const currentLang = localStorage.getItem(StoreKeys.LANG) as "fr" | "en";

  const dilemmaFormStep1Schema = z.object({
    title: z.string().min(1).max(255),
    topic: z.string().min(1, t("modules.dilemmas.errors.topic_required")),
    description: z
      .string()
      .min(1, t("modules.dilemmas.errors.description_required")),
    default_language: z.enum(["fr", "en"]),
  });

  const formStep1 = useForm<z.infer<typeof dilemmaFormStep1Schema>>({
    resolver: zodResolver(dilemmaFormStep1Schema),
    defaultValues: {
      title: step1Data.title,
      topic: step1Data.topic,
      description: step1Data.description,
      default_language: step1Data.default_language || currentLang || "fr",
    },
  });

  const dilemmaLanguage = useWatch({
    control: formStep1.control,
    name: "default_language",
  });
  const selectedTopic = useWatch({ control: formStep1.control, name: "topic" });
  const watchedTitle = useWatch({ control: formStep1.control, name: "title" });
  const watchedDescription = useWatch({
    control: formStep1.control,
    name: "description",
  });

  useEffect(() => {
    setIsStep1Dirty(
      hasStep1Changes(initialDilemma, {
        title: watchedTitle ?? "",
        description: watchedDescription ?? "",
        topic: selectedTopic ?? "",
        default_language: dilemmaLanguage ?? "fr",
      }),
    );
  }, [
    watchedTitle,
    watchedDescription,
    selectedTopic,
    dilemmaLanguage,
    initialDilemma,
    setIsStep1Dirty,
  ]);

  useEffect(() => {
    return () => {
      if (!formStep1.formState.isDirty) return;
      const values = formStep1.getValues();
      setStep1Data({
        title: values.title ?? "",
        topic: values.topic ?? "",
        description: values.description ?? "",
        default_language: values.default_language ?? "fr",
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const canEdit = !createdDilemma || createdDilemma.can_edit;
  const isSubmittedReviewable =
    createdDilemma?.source === "mobile" &&
    createdDilemma?.in_submission_process === true &&
    canEdit &&
    createdDilemma?.can_publish === false;

  async function handleReject() {
    if (!createdDilemma) return;
    setIsLoading(true);
    const [error] = await catchError(
      dilemmasRepo.patch(createdDilemma.id, { status: DILEMMAS_STATUS.DRAFT }),
    );
    if (error) {
      toast.error(handleApiError(error).message);
      setIsLoading(false);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["submissions"] });
    toast.success(t("messages.dilemma_reject_success"));
    reset();
    navigate(ROUTES.SUBMISSIONS);
    setIsLoading(false);
  }

  async function onSubmit(data: z.infer<typeof dilemmaFormStep1Schema>) {
    const isEditing = !!createdDilemma;
    setIsLoading(true);
    const payload = {
      name: data.title,
      description: data.description,
      default_language: data.default_language,
      topic: data.topic,
      status: isEditing ? createdDilemma.status : DILEMMAS_STATUS.DRAFT,
      ...(isEditing ? { review_comment: reviewComment } : {}),
    };

    const [error, dilemma] = await catchError(
      isEditing
        ? dilemmasRepo.patch(createdDilemma.id, payload)
        : dilemmasRepo.create(payload),
    );

    if (error) {
      toast.error(handleApiError(error).message);
    }
    if (dilemma) {
      await queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
      await queryClient.invalidateQueries({ queryKey: ["submissions"] });
      if (isEditing) {
        await queryClient.invalidateQueries({
          queryKey: ["dilemma", createdDilemma.id],
        });
      }
      const [refreshError, refreshedDilemma] = isEditing
        ? await catchError(dilemmasRepo.fetchOne(dilemma.id))
        : [null, dilemma];
      if (refreshError) {
        toast.error(handleApiError(refreshError).message);
        setIsLoading(false);
        return;
      }
      setCreatedDilemma(refreshedDilemma ?? dilemma);
      setStep1Data({
        title: data.title,
        topic: data.topic,
        description: data.description,
        default_language: data.default_language,
      });
      if (isEditMode) {
        await queryClient.invalidateQueries({
          queryKey: ["dilemma", dilemma?.id],
        });
        toast.success(t("messages.dilemma_edit_success"));
      }
      setCurrentStep(currentStep + 1);
    }
    setIsLoading(false);
  }

  return (
    <div className="flex flex-col gap-4">
      <form
        id="dilemma-form"
        onSubmit={formStep1.handleSubmit(onSubmit, () =>
          toast.error(t("modules.dilemmas.errors.form_incomplete")),
        )}
      >
        <FieldGroup>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={!canEdit}
              onClick={() => formStep1.setValue("default_language", "fr")}
              className={cn(
                "cursor-pointer flex flex-1 lg:flex-none items-center gap-3 p-3 py-2 w-36 rounded-md border border-transparent bg-gray-100 text-gray-500",
                dilemmaLanguage === "fr" &&
                  "bg-white text-primary border-primary",
                !canEdit && "opacity-50 cursor-not-allowed",
              )}
            >
              <img src={frIcon} alt="FR" className="size-5" />
              {t("languages.fr")}
            </button>
            <button
              type="button"
              disabled={!canEdit}
              onClick={() => formStep1.setValue("default_language", "en")}
              className={cn(
                "cursor-pointer flex flex-1 lg:flex-none items-center gap-3 p-3 py-2 w-36 rounded-md border border-transparent bg-gray-100 text-gray-500",
                dilemmaLanguage === "en" &&
                  "bg-white text-primary border-primary",
                !canEdit && "opacity-50 cursor-not-allowed",
              )}
            >
              <img src={enIcon} alt="EN" className="size-5" />
              {t("languages.en")}
            </button>
          </div>
          <Controller
            name="title"
            control={formStep1.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="title-input" required>
                  {t("modules.dilemmas.form.title")}
                </FieldLabel>
                <Input
                  {...field}
                  className="h-12"
                  id="title-input"
                  aria-invalid={fieldState.invalid}
                  disabled={!canEdit}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          <Field data-invalid={!!formStep1.formState.errors.topic}>
            <FieldLabel htmlFor="topic-select" required>
              {t("modules.dilemmas.form.pick_a_topic")}
            </FieldLabel>
            {isLoadingThemes && (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton key={index} className="h-12 w-full" />
                ))}
              </div>
            )}
            {!isLoadingThemes && !fetchedThemes.length && (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-gray-500">
                  {t("modules.dilemmas.datatable.no_dilemmas")}
                </p>
                <NavLink to={ROUTES.DILEMMA_CREATE}>
                  <Button variant="primary_light" size="sm">
                    {t("modules.dilemmas.datatable.add_dilemma")}
                  </Button>
                </NavLink>
              </div>
            )}
            {fetchedThemes.length > 0 ? (
              <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-3">
                {fetchedThemes
                  .filter((t) => t.status === "active")
                  ?.map((theme) => (
                    <Button
                      type="button"
                      className="h-12"
                      variant={
                        selectedTopic === theme.id ? "default" : "secondary"
                      }
                      disabled={!canEdit}
                      onClick={() =>
                        formStep1.setValue("topic", theme.id, {
                          shouldValidate: true,
                        })
                      }
                      key={theme.id}
                    >
                      {theme.name}
                    </Button>
                  ))}
              </div>
            ) : null}
            {formStep1.formState.errors.topic && (
              <FieldError errors={[formStep1.formState.errors.topic]} />
            )}
          </Field>
          <Controller
            name="description"
            control={formStep1.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="description-input" required>
                  {t("modules.dilemmas.form.description")}
                </FieldLabel>
                <Textarea
                  {...field}
                  id="description-input"
                  className="min-h-32"
                  aria-invalid={fieldState.invalid}
                  disabled={!canEdit}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
        </FieldGroup>
      </form>
      <BottomFixed className="bg-white">
        <div className="flex items-center justify-end gap-3">
          {isSubmittedReviewable && (
            <Button
              className="flex-1 sm:flex-none"
              variant="primary_light"
              disabled={isLoading}
              type="button"
              onClick={handleReject}
            >
              {isLoading ? (
                <Loader className="animate-spin" />
              ) : (
                <Undo className="size-4" />
              )}
              {t("keys.reject_dilemma")}
            </Button>
          )}
          <Button
            className="flex-1 sm:flex-none"
            disabled={isLoading || !canEdit}
            type="submit"
            form="dilemma-form"
          >
            {isLoading ? <Loader className="animate-spin" /> : <ArrowRight />}
            {t("keys.next")}
          </Button>
        </div>
      </BottomFixed>
    </div>
  );
};

export default DilemmaFormStepOne;
